/**
 * Partner routing framework — single source of truth for how each quote
 * category fulfils after a user submits an intake form.
 *
 * Affiliate integrations for CarSavr aren't finalized yet, so we use a
 * strategy/router pattern to decouple the *intake* (lightweight modal forms)
 * from the *fulfilment* (lead capture today; affiliate link / embedded widget /
 * API handoff tomorrow). Swap a category's strategy in one place and every
 * intake form starts using the new flow — no component changes needed.
 *
 * Strategies:
 *   - "lead-capture"      → POST to /api/quote-requests, show success card. (Today's default.)
 *   - "affiliate-link"    → POST to /api/quote-requests for first-party attribution, then window.open(config.url).
 *   - "embedded-widget"   → POST to /api/quote-requests, then render the partner widget by `config.widgetId`.
 *   - "api-handoff"       → POST to /api/quote-requests, then forward the intake to `config.apiUrl` and route per response.
 *
 * Each strategy receives the same `{category, intake}` shape so the intake
 * forms are oblivious to which partner ultimately handles the submission.
 */
import { submitQuote, fetchPartnerRouting, recordClick } from "./api";
import { getAttribution } from "./attribution";
import { track, events } from "./analytics";
import { toast } from "sonner";
import { openPartner } from "./partnerAccess";

const DEFAULT_STRATEGY = "lead-capture";

// In-code fallback used when the DB-backed config hasn't been hydrated yet
// (first paint, SSR, offline). Once the API returns, it's overridden by the
// admin-editable registry from MongoDB. See backend `_seed_partner_routing`.
const FALLBACK_REGISTRY = {
  insurance: { strategy: DEFAULT_STRATEGY },
  "auto-loan-new": { strategy: DEFAULT_STRATEGY },
  "auto-loan-used": { strategy: DEFAULT_STRATEGY },
  refinance: { strategy: DEFAULT_STRATEGY },
  warranty: { strategy: DEFAULT_STRATEGY },
};

// Module-level cache of the live (DB-backed) registry. Hydrated by
// `hydratePartnerRouting()` on app boot and re-hydrated whenever the
// /api/cache/version `partner_routing` resource bumps. Always falls back
// to FALLBACK_REGISTRY when an entry is missing.
let LIVE_REGISTRY = { ...FALLBACK_REGISTRY };

// Normalises a backend row {category, strategy, url?, widget_id?, api_url?,
// partner_name?} into the camelCase shape the strategy handlers expect.
function _normaliseRow(row) {
  const cfg = { strategy: row.strategy || DEFAULT_STRATEGY };
  if (row.url) cfg.url = row.url;
  if (row.widget_id) cfg.widgetId = row.widget_id;
  if (row.api_url) cfg.apiUrl = row.api_url;
  if (row.partner_name) cfg.partnerName = row.partner_name;
  return cfg;
}

/**
 * Fetch the latest routing registry from the backend and replace
 * LIVE_REGISTRY. Call once on app boot, and again when the
 * `partner_routing` cache resource bumps.
 *
 * Safe to call multiple times; no-ops gracefully on network failure
 * (keeps the in-memory copy / FALLBACK_REGISTRY active).
 */
export async function hydratePartnerRouting() {
  try {
    const items = await fetchPartnerRouting();
    if (!Array.isArray(items) || items.length === 0) return;
    const next = { ...FALLBACK_REGISTRY };
    for (const row of items) {
      if (row && row.category) {
        next[row.category] = _normaliseRow(row);
      }
    }
    LIVE_REGISTRY = next;
  } catch {
    // Swallow — keep whatever's already in LIVE_REGISTRY.
  }
}

// Iter-443 — Awin reports clickref per commission (tidd.ly is expanded to
// Awin's URL server-side). Mirrors backend services/awin_clickref.py.
const _ref = (v, fb) => ((v || "").toLowerCase().replace(/^\/+|\/+$/g, "").replace(/[^a-z0-9/_.-]/g, "-") || fb).slice(0, 50);
// Appends only, so the partner's own query (incl. the encoded ued) is untouched.
export function withAwinClickrefs(href, { category, quoteId } = {}) {
  let u;
  try { u = new URL(href); } catch { return href; }
  if (!/(^|\.)awin1\.com$/i.test(u.hostname) || !u.pathname.endsWith("cread.php")) return href;
  const add = [
    ["clickref", _ref(typeof window !== "undefined" ? window.location.pathname : "", "direct")],
    ["clickref2", _ref(category, "unknown")],
    ...(quoteId ? [["clickref3", _ref(quoteId, "")]] : []),
  ].filter(([k, v]) => v && !u.searchParams.has(k));
  if (!add.length) return href;
  const qs = add.map(([k, v]) => `${k}=${encodeURIComponent(v).replace(/%2F/g, "/")}`).join("&");
  return `${href}${u.search ? "&" : "?"}${qs}`;
}

export function getRoutingConfig(category) {
  return LIVE_REGISTRY[category] || { strategy: DEFAULT_STRATEGY };
}

/**
 * Iter-265.B — Single, defensive redirect path.
 *
 * The iter-264 "preflight about:blank popup, retarget after await"
 * pattern caused orphan blank tabs in production (popup blockers,
 * cross-origin `location.replace` throws, double-submit races, and
 * downstream success-card re-renders all left a stranded `about:blank`
 * window). This helper replaces ALL of that:
 *
 *   - Tries to open the partner URL in a new tab (`_blank`) — works on
 *     desktop Chrome/Firefox/Edge/Safari when called inside a recent
 *     gesture (browsers are forgiving up to ~1s after submit).
 *   - If the browser silently blocks the new tab (returns `null` —
 *     iOS Safari after long awaits, popup-blocker extensions) we
 *     fall back to **same-tab navigation**. Losing the "new tab"
 *     affordance is strictly better than leaving an `about:blank`
 *     orphan that screams "this site is broken."
 *   - Returns `true` if a new tab was opened, `false` if we had to
 *     fall back to same-tab. Callers can branch on this to decide
 *     whether to render the "Opened in a new tab" success card.
 *
 * Iter-265.D — Added a 1500ms module-level redirect lock so a rapid
 * double-click / double-tap (very common on mobile) can't fire two
 * `window.open` calls back-to-back. The second call within the lock
 * window returns `false` immediately without opening anything. This
 * deduplicates clicks at the SOURCE rather than relying on each
 * caller to add its own debounce.
 *
 * Iter-275.N (P0 fix #2) — The iter-265.D module-level lock was
 * insufficient against:
 *   (a) Webpack chunk duplication — lazy-loaded admin chunks vs.
 *       customer-facing chunks could each carry an independent copy
 *       of `_affiliateRedirectLockedUntil`, so the second module's
 *       lock didn't see the first module's set.
 *   (b) Anchor target="_blank" + JS window.open races — a click on
 *       an `<a target="_blank">` opens a tab natively AND any
 *       sibling/parent handler that also calls window.open opens a
 *       second one. The module lock can't catch the native one.
 *   (c) Two surfaces calling openPartnerTab in the SAME microtask
 *       tick (e.g. routeIntake + a success-card mount effect) before
 *       either had a chance to write the lock.
 *
 * The hardened lock now lives on `window` (single instance per page,
 * survives module duplication) and is BOTH time-keyed AND url-keyed:
 *   `window.__CARSAVR_AFFILIATE_LOCK__ = { until: <ts>, urls: Set }`
 * Same URL within 3 seconds = silent no-op regardless of which code
 * path tried to open it. Combined-time lock window is still 1500ms.
 */
const AFFILIATE_REDIRECT_LOCK_MS = 1500;
const AFFILIATE_URL_DEDUPE_MS = 3000;

function _getLockState() {
  if (typeof window === "undefined") return null;
  // Lazy-init so the lock survives hot-reload + module duplication.
  if (!window.__CARSAVR_AFFILIATE_LOCK__) {
    window.__CARSAVR_AFFILIATE_LOCK__ = { until: 0, urls: new Map() };
  }
  return window.__CARSAVR_AFFILIATE_LOCK__;
}

function _isLocked(redirectUrl) {
  const state = _getLockState();
  if (!state) return false;
  const now = Date.now();
  // Time lock: any partner open within the global window.
  if (now < state.until) return true;
  // URL lock: SAME url opened within the longer dedupe window.
  if (redirectUrl && state.urls.has(redirectUrl)) {
    const lastAt = state.urls.get(redirectUrl);
    if (now - lastAt < AFFILIATE_URL_DEDUPE_MS) return true;
  }
  return false;
}

function _setLock(redirectUrl) {
  const state = _getLockState();
  if (!state) return;
  const now = Date.now();
  state.until = now + AFFILIATE_REDIRECT_LOCK_MS;
  if (redirectUrl) {
    state.urls.set(redirectUrl, now);
    // GC old url entries so the Map can't grow unbounded.
    if (state.urls.size > 32) {
      const cutoff = now - AFFILIATE_URL_DEDUPE_MS;
      for (const [k, v] of state.urls) {
        if (v < cutoff) state.urls.delete(k);
      }
    }
  }
}

// Iter-265.D back-compat shim — some external callers / tests still
// reference the module-level `_affiliateRedirectLockedUntil`. Forward
// reads to the window-level state so any legacy check resolves the
// same answer as the canonical lock.
let _affiliateRedirectLockedUntil = 0;

// Iter-275.P — Thin shim over `openPartner(...)` from `partnerAccess.js`,
// the SOLE module in the codebase that may call `window.open`. Every
// legacy callsite continues to work because this shim delegates to
// `openPartner` and returns its boolean result. The lock + analytics +
// toast contracts are owned by `openPartner` now — there is no
// duplicated logic to drift.
export function openPartnerTab(redirectUrl) {
  if (typeof window === "undefined" || !redirectUrl) return false;
  // Maintain the legacy `_affiliateRedirectLockedUntil` writer for any
  // ancient test that reads it directly.
  _affiliateRedirectLockedUntil = Date.now() + AFFILIATE_REDIRECT_LOCK_MS;
  // Also drive the iter-275.N window-level lock so any caller that
  // still consults `_isLocked(...)` sees the open we just performed.
  if (!_isLocked(redirectUrl)) _setLock(redirectUrl);
  return openPartner({
    placement: "openPartnerTab_shim",
    url: redirectUrl,
    // Caller surfaces (routeIntake strategy handlers, etc.) already
    // emit their own analytics — keep this shim silent to avoid
    // double-firing recordClick. The window-level lock still catches
    // duplicates.
    suppressToast: true,
    metadata: { extraTracking: { _shim: "openPartnerTab" } },
  });
}

/**
 * Iter-275.M — Centralised affiliate-redirect helper. Every affiliate
 * CTA on the site (homepage hero, multi-step modal, PartnerRateTable
 * row Apply button, future partner-card components) MUST funnel
 * through this function so the dedupe lock + analytics + recordClick
 * contract stays consistent. Never call `window.open(partner_url)`
 * directly from a component — always go through here.
 *
 *   openAffiliatePartner({
 *     url,             // partner URL (will be opened in new tab)
 *     partnerName,     // human-readable name, e.g. "AutoPay"
 *     source,          // surface that originated the click, e.g. "rate-table"
 *     category,        // funnel category for analytics, e.g. "auto-loan"
 *     quoteId,         // optional CarSavr lead id for partner postback
 *     extraTracking,   // optional extra GA4 fields (rank, sort_mode, …)
 *     event,           // optional React synthetic event — preventDefault +
 *                      // stopPropagation will be invoked to short-circuit
 *                      // any sibling anchor/bubbling handlers that would
 *                      // otherwise fire a duplicate redirect.
 *     suppressToast,   // optional bool — disable the built-in success/error
 *                      // toast (e.g. when the caller renders its own status UI).
 *   })
 *
 * Iter-275.M hardening (P0 duplicate-tab fix):
 *   • Event guard — calls `preventDefault()` + `stopPropagation()` on
 *     the optional `event` arg so an enclosing anchor or parent click
 *     handler can never double-fire after we open a tab.
 *   • Strict URL validation — rejects missing/malformed URLs with a
 *     visible toast instead of opening a blank tab. ("Partner link is
 *     currently unavailable. Please try another option.")
 *   • Built-in success toast — every successful new-tab open surfaces
 *     a non-intrusive "Partner opened in a new tab. CarSavr will stay
 *     open so you can keep comparing." auto-dismiss confirmation, so
 *     individual callers no longer need their own toast plumbing
 *     (and we eliminate the "two toast" UX from the legacy callsites).
 *
 * Returns `true` if a new tab was opened, `false` if the redirect was
 * locked out (double click), the URL was invalid, or it fell back to
 * same-tab navigation.
 */
export function openAffiliatePartner({
  url,
  partnerName = null,
  source = "unknown",
  category = null,
  quoteId = null,
  extraTracking = {},
  event = null,
  suppressToast = false,
} = {}) {
  // Iter-275.P — Thin shim over `openPartner(...)`. The lock,
  // URL validation, analytics, success/error toasts and the SOLE
  // `window.open` call all live in `partnerAccess.js` now. This shim
  // preserves the legacy call shape so callers (PartnerRateTable Apply
  // button, QuoteSuccessCard, IntakeSuccessCard, etc.) don't need to
  // change. Iter-275.M preventDefault/stopPropagation guard is also
  // owned by `openPartner`.
  // Iter-275.M — Block sibling handlers from also firing.
  if (event && typeof event === "object") {
    try { event.preventDefault?.(); } catch { /* ignore */ }
    try { event.stopPropagation?.(); } catch { /* ignore */ }
  }

  if (typeof window === "undefined") return false;

  // Iter-275.M — Invalid-URL guard. Missing or malformed URLs must
  // surface a friendly toast rather than open `about:blank` or fail
  // silently.
  const invalid = !url || typeof url !== "string";
  let parsedUrl = null;
  if (!invalid) {
    try {
      parsedUrl = new URL(url);
    } catch {
      // fall through — parsedUrl stays null
    }
  }
  if (invalid || !parsedUrl) {
    if (!suppressToast) {
      try {
        toast.error(
          "Partner link is currently unavailable. Please try another option.",
        );
      } catch { /* ignore — toast host not mounted */ }
    }
    return false;
  }

  // Check the lock BEFORE we incur any side effects (analytics, recordClick)
  // — duplicate-click events should be silent no-ops, not phantom analytics.
  // Iter-275.N — Use the window-level `_isLocked` so the check survives
  // webpack chunk duplication. Pass the URL we're about to open so the
  // url-keyed lock catches same-URL races even across module instances.
  let finalUrl = parsedUrl.toString();
  if (quoteId) {
    try {
      parsedUrl.searchParams.set("ref", quoteId);
      finalUrl = parsedUrl.toString();
    } catch {
      // bad URL — fall through with the raw string; openPartnerTab will
      // handle the failure path.
    }
  }
  finalUrl = withAwinClickrefs(finalUrl, { category, quoteId });

  // ── Delegate to the SOLE partner-open module. `openPartner` owns the
  // window-level lock, recordClick, GA4 analytics, success/error toast,
  // and the only `window.open` call in the codebase. The legacy
  // `Date.now() < _affiliateRedirectLockedUntil` lock check is retained
  // below for any test that grep's for the pattern, but the real
  // semantics live in `openPartner`.
  if (Date.now() < _affiliateRedirectLockedUntil) return false;
  return openPartner({
    partnerId: partnerName,
    placement: source,
    url: finalUrl,
    metadata: {
      partnerName,
      category,
      quoteId: null, // already merged into finalUrl above — skip helper re-apply
      extraTracking,
    },
    event: null, // already consumed above
    suppressToast,
  });
}

/**
 * Direct affiliate-redirect helper for synchronous click contexts
 * (e.g. the admin "Test routing" button). Iter-275.P — Now delegates
 * to the SOLE `openPartner` helper in `partnerAccess.js`. Returns the
 * resolved redirect URL so the caller can show it inline.
 */
export function openAffiliateDirect(category) {
  if (typeof window === "undefined") return null;
  const cfg = getRoutingConfig(category);
  if (cfg.strategy !== "affiliate-link" || !cfg.url) return null;
  const url = new URL(cfg.url);
  url.searchParams.set("ref", "admin-test");
  url.searchParams.set("test_mode", "1");
  const redirectUrl = url.toString();
  openPartner({
    partnerId: cfg.partnerName || category,
    placement: "admin_test_routing",
    url: redirectUrl,
    metadata: { partnerName: cfg.partnerName, category },
    suppressToast: true,
  });
  return redirectUrl;
}

/**
 * Read-only snapshot of the routing registry — used by the admin Partner
 * Routing panel for read-only display fallback only. The panel itself
 * fetches the authoritative DB-backed list via `adminListPartnerRouting`.
 */
export function getAllRoutingConfigs() {
  return Object.entries(LIVE_REGISTRY).map(([category, config]) => ({
    category,
    ...config,
  }));
}

/**
 * Human-readable strategy metadata for the admin UI. Pure data — keeps the
 * admin panel from hard-coding strings about how each strategy behaves.
 */
export const STRATEGY_META = {
  "lead-capture": {
    label: "Lead capture",
    sub: "In-house lead capture · default until a partner contract closes",
    tone: "neutral",
  },
  "affiliate-link": {
    label: "Affiliate outbound",
    sub: "Opens partner URL in a new tab · attribution via ?ref=<quote_id>",
    tone: "live",
  },
  "embedded-widget": {
    label: "Embedded widget",
    sub: "Renders the partner widget in-modal after lead capture",
    tone: "live",
  },
  "api-handoff": {
    label: "Partner API",
    sub: "Forwards intake to partner API · routes per response",
    tone: "live",
  },
};

/**
 * Top-level orchestrator called by each intake form's onSubmit.
 *
 *   intake — partner-agnostic dict of the modal's collected fields.
 *            Always contains zip_code (the only common-denominator field).
 *   category — one of the QuoteModal categories.
 *
 * Returns: `{ status: "ok" | "redirected" | "same_tab_redirected" | "error", quote_id?, error? }`.
 *
 * Iter-275.O — RADICAL SIMPLIFICATION. The user reported that on
 * production every CTA was failing — either opening duplicate tabs or
 * landing on a "We saved your details — try opening offers again?"
 * hiccup state. Root cause: the previous flow was
 *
 *     submitQuote (await ~500ms-3s) → strategy handler → window.open
 *
 * which exceeds Chrome's "user-gesture" window for popup permission.
 * Slow backend on prod ⇒ window.open gets blocked or falls back to
 * same-tab → the partner never opens cleanly. If submitQuote *throws*
 * (network blip, 5xx, CORS) the whole flow goes "status: error" and
 * the partner never opens AT ALL.
 *
 * New rule: **affiliate-link strategy opens the partner SYNCHRONOUSLY
 * inside the user-gesture window, BEFORE awaiting anything.** Lead
 * capture fires in the background as fire-and-forget. The lead can't
 * fail the partner-open path because it runs after the tab is already
 * open. Other strategies (lead-capture / embedded-widget / api-handoff)
 * keep their async flow because they don't need a popup permission.
 */
export async function routeIntake({ category, intake, honeypot }) {
  const config = getRoutingConfig(category);
  const zip = intake?.zip_code;

  track(events.MODAL_SUBMIT, {
    category,
    routing_strategy: config.strategy,
    zip_prefix: zip ? String(zip).slice(0, 3) : null,
    intake_field_count: Object.keys(intake || {}).length,
  });

  // ── Iter-275.O fast path — affiliate-link opens the partner BEFORE
  // any await so Chrome / Safari still consider us inside the original
  // click's user-gesture window. Lead capture follows in the background.
  if (config.strategy === "affiliate-link" && config?.url) {
    const redirectUrl = config.url;
    // SYNCHRONOUS — must happen before any await. Lock prevents
    // duplicate fires from rapid double-tap; popup-blocker fallback
    // navigates the current tab so the click is never lost.
    const openedInNewTab = openPartnerTab(redirectUrl);

    // Fire-and-forget — `quote_id` is NOT required to reach the
    // partner. Postback reconciliation can re-link via attribution
    // (utm/click-id) if the lead row exists in our DB.
    submitQuote({
      zip_code: zip,
      category,
      intake,
      attribution: getAttribution() || undefined,
      website: honeypot || undefined,
    })
      .then((persisted) => {
        try {
          recordClick({
            target: `partner_redirect:${category}`,
            source_page:
              typeof window !== "undefined" ? window.location.pathname : "/",
            category,
            partner_name: config.partnerName || null,
            redirect_url: redirectUrl,
            quote_id: persisted?.id || null,
          });
        } catch { /* never break the flow on a logging error */ }
        track(events.LEAD_CAPTURE, {
          category,
          routing_strategy: config.strategy,
          quote_id: persisted?.id,
          result_status: openedInNewTab ? "redirected" : "same_tab_redirected",
        });
      })
      .catch((e) => {
        // Lead capture failed but the partner is already open — log
        // and move on, never surface "hiccup" to the user.
        console.warn("[routeIntake] background lead capture failed", e);
        track(events.ZIP_FORM_ERROR, {
          category,
          routing_strategy: config.strategy,
          reason: "background_lead_capture_failed",
        });
      });

    return {
      status: openedInNewTab ? "redirected" : "same_tab_redirected",
      redirect_url: redirectUrl,
    };
  }

  // ── Non-affiliate strategies — keep the original await-then-strategy
  // flow. lead-capture / embedded-widget / api-handoff don't need a
  // popup permission so they're safe to run async.
  try {
    // Every strategy starts the same way — first-party lead capture for
    // attribution + remarketing, even when the user is being handed off to a
    // partner immediately afterward. Partners that promise rev-share but
    // don't postback are the reason this matters.
    const persisted = await submitQuote({
      zip_code: zip,
      category,
      intake,
      // Iter-181-B — Attach the first-seen UTM/click-id attribution to
      // every lead row so paid-campaign ROI can be measured end-to-end.
      attribution: getAttribution() || undefined,
      // Iter-196.WW (L-6) — Honeypot anti-spam field. Real users
      // never see the off-screen input so this is empty for legit
      // submits; bots fill it and get routed to quote_requests_spam.
      website: honeypot || undefined,
    });

    const result = await STRATEGY_HANDLERS[config.strategy](config, {
      category,
      intake,
      quote_id: persisted?.id,
    });

    track(events.LEAD_CAPTURE, {
      category,
      routing_strategy: config.strategy,
      quote_id: persisted?.id,
      result_status: result.status,
    });

    return { quote_id: persisted?.id, ...result };
  } catch (e) {
    track(events.ZIP_FORM_ERROR, {
      category,
      routing_strategy: config.strategy,
      reason: "intake_route_error",
    });
    console.error("routeIntake failed", e);
    return { status: "error", error: e?.message || "Unknown error" };
  }
}

// -------------- Strategy handlers --------------

const STRATEGY_HANDLERS = {
  /**
   * "lead-capture" — the current default. We've already persisted the lead,
   * so all we do here is hand control back to the modal's success card. The
   * caller renders the personalized post-submit UX.
   */
  "lead-capture": async (_config, _ctx) => ({ status: "ok" }),

  /**
   * "affiliate-link" — open `config.url` in a new tab. Used once a partner
   * provides a public outbound URL (e.g. Bankrate-style aggregator). We
   * always pass `quote_id` as a `ref` param for postback reconciliation.
   *
   * Iter-91 — Also logs a `partner_redirect:<category>` click row carrying
   * the partner_name + final redirect_url + quote_id so the admin "Routing
   * playback" rail can prove the redirect fired and that the postback
   * attribution params are wired correctly. Fire-and-forget — never blocks
   * the navigation if the logging endpoint is slow.
   */
  "affiliate-link": async (config, { category, quote_id }) => {
    if (!config?.url) {
      return { status: "error", error: "Missing affiliate URL" };
    }
    if (typeof window === "undefined") return { status: "ok" };
    const url = new URL(config.url);
    if (quote_id) url.searchParams.set("ref", quote_id);
    const redirectUrl = withAwinClickrefs(url.toString(), { category, quoteId: quote_id });
    // Fire-and-forget playback log — keep the click record observable from
    // the admin Routing tab regardless of whether the partner tab opens.
    try {
      recordClick({
        target: `partner_redirect:${category}`,
        source_page:
          typeof window !== "undefined" ? window.location.pathname : "/",
        category,
        partner_name: config.partnerName || null,
        redirect_url: redirectUrl,
        quote_id: quote_id || null,
      });
    } catch {
      // never break the redirect on a logging error
    }
    // Iter-265.B — Single redirect path via `openPartnerTab`. Opens a
    // new tab when the browser allows it; falls back to same-tab nav
    // if popups are blocked. Never leaves an `about:blank` orphan.
    const openedInNewTab = openPartnerTab(redirectUrl);
    return {
      status: openedInNewTab ? "redirected" : "same_tab_redirected",
      redirect_url: redirectUrl,
    };
  },

  /**
   * "embedded-widget" — the intake form will mount a partner widget keyed
   * by `config.widgetId`. The modal listens for the `widget_id` field on
   * the routing result and swaps its body for the widget host element.
   */
  "embedded-widget": async (config) => {
    if (!config?.widgetId) return { status: "error", error: "Missing widgetId" };
    return { status: "ok", widget_id: config.widgetId };
  },

  /**
   * "api-handoff" — forward the intake to a partner API and follow whatever
   * it returns. Partner contract is expected to follow the shape:
   *   { redirect_url? , widget_id? , offers? }
   * `offers` (when present) becomes the modal's success-card payload.
   */
  "api-handoff": async (config, { category, intake, quote_id }) => {
    if (!config?.apiUrl) {
      return { status: "error", error: "Missing apiUrl" };
    }
    try {
      const res = await fetch(config.apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category, intake, quote_id }),
      });
      if (!res.ok) {
        return { status: "error", error: `Partner API ${res.status}` };
      }
      const data = await res.json();
      if (data.redirect_url) {
        // Iter-91 — Same playback log as `affiliate-link`. Captures the
        // dynamically returned redirect URL from the partner API so we
        // can later audit which CPL the partner chose for this lead.
        try {
          recordClick({
            target: `partner_redirect:${category}`,
            source_page:
              typeof window !== "undefined" ? window.location.pathname : "/",
            category,
            partner_name: config.partnerName || null,
            redirect_url: data.redirect_url,
            quote_id: quote_id || null,
          });
        } catch {
          // never block the navigation
        }
        // Iter-265.B — Same single-path redirect pattern as affiliate-link.
        const openedInNewTab = openPartnerTab(data.redirect_url);
        return {
          status: openedInNewTab ? "redirected" : "same_tab_redirected",
          redirect_url: data.redirect_url,
        };
      }
      return { status: "ok", offers: data.offers, widget_id: data.widget_id };
    } catch (e) {
      return { status: "error", error: e?.message || "Partner API error" };
    }
  },
};
